import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [email, setEmail] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaSvg, setCaptchaSvg] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordChecks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[@$!%*?&#]/.test(password) && /^[A-Za-z\d@$!%*?&#]+$/.test(password)
  };
  const passwordStrength = Object.values(passwordChecks).filter(Boolean).length;

  const fetchCaptcha = async () => {
    try {
      const response = await api.get('/auth/captcha');
      setCaptchaId(response.data.captchaId);
      setCaptchaSvg(response.data.svg);
    } catch (err) {
      console.error('获取验证码失败:', err);
    }
  };

  useEffect(() => {
    fetchCaptcha();
  }, []);

  const sendEmailCode = async () => {
    if (!email || !captchaText) {
      setError('请填写邮箱和图形验证码');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/auth/send-email-code', {
        email,
        captchaId,
        captchaText
      });
      
      if (response.data.code) {
        setEmailCode(response.data.code);
      }
      
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (err) {
      setError(err.response?.data?.error || '发送验证码失败');
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailCode = () => {
    if (!emailCode || emailCode.length !== 6) {
      setError('请输入6位邮箱验证码');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleRegister = async () => {
    setError('');
    
    if (nickname && (nickname.length > 20 || !/^[\u4e00-\u9fa5a-zA-Z]/.test(nickname))) {
      setError('昵称必须以中文或字母开头，且不超过20个字符');
      return;
    }
    
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
    if (!passwordRegex.test(password)) {
      setError('密码必须包含大小写字母、数字，特殊字符只能包含（@$!%*?&#），且至少8位');
      return;
    }
    if (passwordStrength < 5) {
      setError('密码不符合要求');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        nickname: nickname || undefined,
        emailCode
      });
      
      localStorage.setItem('user_token', response.data.token);
      navigate('/dashboard');
      
    } catch (err) {
      setError(err.response?.data?.error || '注册失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-10 flex items-center justify-center">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">创建账号</h1>
          <p className="text-gray-400">加入 NCB Platform，开始零代码建站之旅</p>
        </div>

        <div className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {step === 1 ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">邮箱</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                  placeholder="请输入邮箱"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">图形验证码</label>
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={captchaText}
                    onChange={(e) => setCaptchaText(e.target.value)}
                    className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    placeholder="请输入验证码"
                    maxLength={4}
                  />
                  <div 
                    className="w-28 h-12 bg-gray-700 rounded-lg flex items-center justify-center cursor-pointer overflow-hidden"
                    onClick={fetchCaptcha}
                    dangerouslySetInnerHTML={{ __html: captchaSvg }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">邮箱验证码</label>
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value)}
                    className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    placeholder="请输入6位验证码"
                    maxLength={6}
                  />
                  <button
                    onClick={sendEmailCode}
                    disabled={loading || countdown > 0}
                    className="px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg text-sm whitespace-nowrap"
                  >
                    {countdown > 0 ? `${countdown}秒` : '发送验证码'}
                  </button>
                </div>
              </div>

              <button
                onClick={verifyEmailCode}
                disabled={!emailCode || emailCode.length !== 6}
                className="w-full py-3 gradient-bg text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-all"
              >
                下一步
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center text-sm text-gray-400 mb-4">
                邮箱: <span className="text-white">{email}</span>
                <button onClick={() => setStep(1)} className="ml-2 text-blue-400 hover:underline">修改</button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">昵称（选填）</label>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                  placeholder="中文或字母开头，20字符以内"
                  maxLength={20}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">密码</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 pr-10"
                    placeholder="请输入密码"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                
                <div className="mt-2 space-y-1">
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded ${
                          passwordStrength >= i
                            ? passwordStrength <= 2 ? 'bg-red-500' : passwordStrength <= 4 ? 'bg-yellow-500' : 'bg-green-500'
                            : 'bg-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <div className={passwordChecks.length ? 'text-green-400' : 'text-gray-500'}>
                      {passwordChecks.length ? <CheckCircleIcon className="w-3 h-3 inline mr-1" /> : <XCircleIcon className="w-3 h-3 inline mr-1" />}
                      至少8位
                    </div>
                    <div className={passwordChecks.lowercase ? 'text-green-400' : 'text-gray-500'}>
                      {passwordChecks.lowercase ? <CheckCircleIcon className="w-3 h-3 inline mr-1" /> : <XCircleIcon className="w-3 h-3 inline mr-1" />}
                      小写字母
                    </div>
                    <div className={passwordChecks.uppercase ? 'text-green-400' : 'text-gray-500'}>
                      {passwordChecks.uppercase ? <CheckCircleIcon className="w-3 h-3 inline mr-1" /> : <XCircleIcon className="w-3 h-3 inline mr-1" />}
                      大写字母
                    </div>
                    <div className={passwordChecks.number ? 'text-green-400' : 'text-gray-500'}>
                      {passwordChecks.number ? <CheckCircleIcon className="w-3 h-3 inline mr-1" /> : <XCircleIcon className="w-3 h-3 inline mr-1" />}
                      数字
                    </div>
                    <div className={passwordChecks.special ? 'text-green-400' : 'text-gray-500'}>
                      {passwordChecks.special ? <CheckCircleIcon className="w-3 h-3 inline mr-1" /> : <XCircleIcon className="w-3 h-3 inline mr-1" />}
                      特殊字符只能包含（@$!%*?&#）
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">确认密码</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 pr-10"
                    placeholder="请再次输入密码"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showConfirmPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-red-400 text-xs mt-1">两次输入的密码不一致</p>
                )}
              </div>

              <button
                onClick={handleRegister}
                disabled={loading || passwordStrength < 5 || password !== confirmPassword}
                className="w-full py-3 gradient-bg text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-all"
              >
                {loading ? '注册中...' : '完成注册'}
              </button>
            </div>
          )}

          <div className="mt-6 text-center text-sm text-gray-400">
            已有账号？
            <Link to="/login" className="text-blue-400 hover:underline ml-1">立即登录</Link>
          </div>
        </div>
      </div>
    </div>
  );
}